import { Alert, Box, Button, MenuItem, Select, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { type EmailMessage, emailTemplates } from "../../data/api";
import { useAction, useLoad } from "../../data/data";
import { StatusBadge } from "../../ui/components/StatusBadge";
import {
  Failure,
  Field,
  FormRow,
  Hint,
  Loading,
  Mono,
  PageHeader,
  Section,
  Tag,
  Tags,
} from "../../ui/components/primitives";
import { useTokens } from "../../ui/theme/theme";
import type { TenantPageProps } from "../pages";

// ---
// The words this tenant's own messages are sent in
//
// One message at a time, because a tenant has two and editing both at once would need a list of
// forms where a chooser does. Each opens on the text actually in force — the tenant's, or the
// default — so customising starts from what people are receiving today rather than a blank box.
//
// The variables come from the server with the message. Keeping a copy here would be a second
// table to drift from the one that does the rendering, and the drift would be discovered in
// somebody's mailbox.
// ---

export function EmailTemplates({ tenant, token, scopes }: TenantPageProps): React.JSX.Element {
  const tokens = useTokens();
  const list = useLoad(() => emailTemplates.list(tenant, token), [tenant, token]);
  const [chosen, setChosen] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ subject: string; body: string } | null>(null);
  const [preview, setPreview] = useState<{ subject: string; body: string } | null>(null);
  const save = useAction(() => list.reload());
  const revert = useAction(() => list.reload());
  const previewing = useAction();

  const messages = list.data ?? [];
  const current: EmailMessage | undefined =
    messages.find((each) => each.message === chosen) ?? messages[0];
  const canManage = scopes.includes("user-flow:manage");

  // The draft follows whichever message is selected, and resets to what is stored whenever the
  // list reloads — so saving, reverting and switching all leave the editor showing the truth.
  useEffect(() => {
    if (current === undefined) return;
    setDraft({ subject: current.subject, body: current.body });
    setPreview(null);
  }, [current]);

  if (list.pending) return <Loading what="templates" />;
  if (current === undefined || draft === null) {
    return (
      <>
        <PageHeader title="Email templates" />
        <Failure error={list.error} />
      </>
    );
  }

  const changed = draft.subject !== current.subject || draft.body !== current.body;

  return (
    <>
      <PageHeader
        title="Email templates"
        action={
          <Select
            size="small"
            value={current.message}
            onChange={(event) => setChosen(event.target.value)}
            sx={{ minWidth: 200 }}
          >
            {messages.map((each) => (
              <MenuItem key={each.message} value={each.message}>
                {each.label}
              </MenuItem>
            ))}
          </Select>
        }
      />

      <Section
        title={current.label}
        action={
          <StatusBadge
            label={current.customised ? "Customised" : "Default"}
            tone={current.customised ? "success" : "neutral"}
          />
        }
      >
        <Hint sx={{ mt: 0, mb: 4 }}>
          {current.customised
            ? `Last changed ${new Date(current.updatedAt ?? "").toLocaleString()}.`
            : "Nobody has changed this, so it is sent in the words FlexAuth ships."}{" "}
          A short line naming FlexAuth and this tenant is added below the body and cannot be edited.
        </Hint>

        <FormRow>
          <Field label="Subject" sx={{ flex: "1 1 100%" }}>
            <TextField
              size="small"
              value={draft.subject}
              disabled={!canManage}
              onChange={(event) => setDraft({ ...draft, subject: event.target.value })}
            />
          </Field>
        </FormRow>

        <FormRow sx={{ mt: 3 }}>
          <Field label="Body" sx={{ flex: "1 1 100%" }}>
            <TextField
              multiline
              minRows={10}
              value={draft.body}
              disabled={!canManage}
              onChange={(event) => setDraft({ ...draft, body: event.target.value })}
              spellCheck={false}
              InputProps={{
                sx: { fontFamily: tokens.font.mono, fontSize: 12.5, lineHeight: 1.7 },
              }}
            />
          </Field>
        </FormRow>

        <Box sx={{ mt: 4 }}>
          <Box sx={{ typography: "body2", color: tokens.ink.secondary, mb: 2 }}>
            Write a variable as <Mono>{"{{name}}"}</Mono>. Anything else between braces is refused
            when you save, rather than arriving blank.
          </Box>
          <Tags>
            {current.variables.map((variable) => (
              <Tag
                key={variable.name}
                label={`{{${variable.name}}}${variable.required ? " · required" : ""}`}
              />
            ))}
          </Tags>
        </Box>

        <Failure error={save.error ?? revert.error ?? previewing.error} />

        <FormRow sx={{ mt: 5, justifyContent: "flex-end" }}>
          <Button
            variant="text"
            disabled={previewing.busy}
            onClick={() =>
              previewing.run(async () => {
                setPreview(
                  await emailTemplates.preview(tenant, token, {
                    message: current.message,
                    ...draft,
                  }),
                );
              })
            }
          >
            Preview
          </Button>
          {canManage && current.customised && (
            <Button
              variant="outlined"
              disabled={revert.busy}
              onClick={() =>
                revert.run(() => emailTemplates.revert(tenant, token, current.message))
              }
            >
              Revert to default
            </Button>
          )}
          {canManage && (
            <Button
              variant="contained"
              disabled={!changed || save.busy}
              onClick={() =>
                save.run(() =>
                  emailTemplates.save(tenant, token, { message: current.message, ...draft }),
                )
              }
            >
              Save
            </Button>
          )}
        </FormRow>
      </Section>

      {preview !== null && (
        <Section title="Preview">
          <Alert severity="info" sx={{ mb: 3 }}>
            Example values, and nothing was sent.
          </Alert>
          <Box sx={{ typography: "body2", color: tokens.ink.secondary, mb: 2 }}>
            Subject: <strong>{preview.subject}</strong>
          </Box>
          <Mono sx={{ whiteSpace: "pre-wrap", fontSize: 12.5, lineHeight: 1.7 }}>
            {preview.body}
          </Mono>
        </Section>
      )}
    </>
  );
}
